'use client';

import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { X, ArrowRight, KeyRound, LogOut, User, FolderOpen, Sparkles, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/auth/auth-provider';
import { useUpgrade } from '@/components/upgrade/UpgradeModal';
import { createClient } from '@/lib/supabase/client';

const FeedbackModal = lazy(() => import('@/components/builder/FeedbackModal').then(mod => ({ default: mod.FeedbackModal })));

export default function HeaderInteractive() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  const { user, isPaid, signOut } = useAuth();
  const { open: openUpgrade } = useUpgrade();
  const [portalLoading, setPortalLoading] = useState(false);

  // Send subscribers to the Stripe Customer Portal to cancel, switch plan,
  // or update their card - no bespoke billing UI to maintain.
  const openBillingPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const res = await fetch('/api/stripe/portal', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        window.location.href = data.url;
        return;
      }
      alert(data.error || 'Could not open the billing portal. Please try again.');
    } catch {
      alert('Could not open the billing portal. Please try again.');
    }
    setPortalLoading(false);
  }, []);

  // Scroll shadow
  useEffect(() => {
    const header = document.querySelector<HTMLElement>('.header-fixed');
    if (!header) return;
    let rafId = 0;
    const update = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        header.style.boxShadow = window.scrollY > 0 ? '0 1px 3px rgba(0,0,0,0.05)' : 'none';
        rafId = 0;
      });
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => { window.removeEventListener('scroll', update); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  // Mobile menu button
  useEffect(() => {
    const btn = document.querySelector<HTMLElement>('.mobile-menu-button');
    if (!btn) return;
    const handler = () => setIsMobileMenuOpen(true);
    btn.addEventListener('click', handler);
    return () => btn.removeEventListener('click', handler);
  }, []);

  // Builder home icon
  useEffect(() => {
    const btn = document.querySelector<HTMLElement>('.builder-home-icon');
    if (!btn) return;
    const handler = () => { if (window.confirm('Leave the editor? Unsaved changes may be lost.')) window.location.href = '/'; };
    btn.addEventListener('click', handler);
    return () => btn.removeEventListener('click', handler);
  }, []);

  // Feedback button
  useEffect(() => {
    const btn = document.querySelector<HTMLElement>('.header-feedback-btn');
    if (!btn) return;
    const handler = () => setShowFeedback(true);
    btn.addEventListener('click', handler);
    return () => btn.removeEventListener('click', handler);
  }, []);

  // Click outside account menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) setShowAccountMenu(false);
    };
    if (showAccountMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAccountMenu]);

  // Lock body when mobile menu open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => { document.body.style.position = ''; document.body.style.top = ''; document.body.style.width = ''; document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const handlePasswordReset = useCallback(async () => {
    if (!user?.email) return;
    const supabase = createClient();
    await supabase.auth.resetPasswordForEmail(user.email, { redirectTo: `${window.location.origin}/auth/callback?next=/auth/reset-password` });
    setResetSent(true);
    setTimeout(() => setResetSent(false), 4000);
  }, [user?.email]);

  // Render account menu into slot
  useEffect(() => {
    const slot = document.getElementById('header-account-slot');
    if (!slot) return;
    slot.style.display = user ? 'block' : 'none';
  }, [user]);

  // Resolve the My Templates portal slots once mounted on the client
  const [myTplDesktopSlot, setMyTplDesktopSlot] = useState<HTMLElement | null>(null);
  const [myTplMobileSlot, setMyTplMobileSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setMyTplDesktopSlot(document.getElementById('header-my-templates-desktop-slot'));
    setMyTplMobileSlot(document.getElementById('header-my-templates-mobile-slot'));
  }, []);

  return (
    <>
      {/* My Templates desktop nav link (portaled into the desktop nav slot) */}
      {user && myTplDesktopSlot && createPortal(
        <Link
          href="/my-templates"
          style={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '16px', color: '#2D2D2D', textDecoration: 'none' }}
        >
          My templates
        </Link>,
        myTplDesktopSlot
      )}

      {/* My Templates mobile icon button (portaled into the mobile header slot) */}
      {user && myTplMobileSlot && createPortal(
        <Link
          href="/my-templates"
          aria-label="My templates"
          className="mobile-only-button"
          style={{ background: 'none', border: '1px solid #E5E5E5', borderRadius: '4px', padding: '8px', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#2D2D2D', textDecoration: 'none' }}
        >
          <FolderOpen className="w-4 h-4" />
        </Link>,
        myTplMobileSlot
      )}

      {/* Desktop Account Menu — rendered next to header via portal-like approach */}
      {user && (
        <div ref={accountMenuRef} className="header-account-menu-wrap desktop-button" style={{ position: 'fixed', top: '14px', right: '75px', zIndex: 1001 }}>
          <button
            onClick={() => setShowAccountMenu(!showAccountMenu)}
            style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#0068D6', color: '#FFF', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, fontFamily: 'Roboto' }}
            aria-label="Account menu"
          >
            {user.email?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
          </button>
          {showAccountMenu && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', backgroundColor: '#FFF', borderRadius: '8px', border: '1px solid #E5E5E5', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: '220px', zIndex: 1002, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #F0F0F0' }}>
                <p style={{ fontSize: '13px', color: '#6B6B6B', margin: 0 }}>Signed in as</p>
                <p style={{ fontSize: '14px', color: '#2D2D2D', fontWeight: 500, margin: '2px 0 0', wordBreak: 'break-all' }}>{user.email}</p>
              </div>
              {!user.identities?.some(i => i.provider === 'google') && (
                <button onClick={handlePasswordReset} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#2D2D2D', fontFamily: 'Roboto', textAlign: 'left' }}>
                  <KeyRound className="w-4 h-4" style={{ color: '#6B6B6B' }} />
                  {resetSent ? 'Check your email!' : 'Change password'}
                </button>
              )}
              {isPaid ? (
                <button onClick={openBillingPortal} disabled={portalLoading} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', borderTop: '1px solid #F0F0F0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#2D2D2D', fontFamily: 'Roboto', textAlign: 'left' }}>
                  <CreditCard className="w-4 h-4" style={{ color: '#6B6B6B' }} />
                  {portalLoading ? 'Opening...' : 'Manage subscription'}
                </button>
              ) : (
                <button
                  onClick={() => { setShowAccountMenu(false); openUpgrade(); }}
                  style={{ width: '100%', padding: '10px 16px', border: 'none', borderTop: '1px solid #F0F0F0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#0b4f92', backgroundColor: '#F0F8FF', fontFamily: 'Roboto', textAlign: 'left', fontWeight: 500 }}
                >
                  <Sparkles className="w-4 h-4" style={{ color: '#0068D6' }} />
                  Upgrade to Pro
                </button>
              )}
              <button onClick={() => { setShowAccountMenu(false); signOut(); }} style={{ width: '100%', padding: '10px 16px', background: 'none', border: 'none', borderTop: '1px solid #F0F0F0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#D32F2F', fontFamily: 'Roboto', textAlign: 'left' }}>
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            </div>
          )}
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedback && (
        <Suspense fallback={null}>
          <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} />
        </Suspense>
      )}

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-menu-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#FFF', zIndex: 1001, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <div style={{ position: 'sticky', top: 0, backgroundColor: '#FFF', padding: '20px', display: 'flex', justifyContent: 'flex-end', zIndex: 1 }}>
            <button onClick={() => setIsMobileMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Close menu">
              <X className="w-6 h-6" style={{ stroke: '#2D2D2D' }} />
            </button>
          </div>
          <nav className="flex flex-col items-center gap-8 p-8 pt-4">
            <Link href="/templates" style={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '24px', color: '#2D2D2D', textDecoration: 'none' }} onClick={() => setIsMobileMenuOpen(false)}>Templates</Link>
            <Link href="/builder" prefetch={false} onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="font-semibold flex items-center justify-center gap-2 transition-colors mt-4" style={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: '18px', padding: '14px 28px', borderRadius: '4px', backgroundColor: '#0068D6', color: '#FFF', border: 'none' }}>
                Make a receipt
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            {user && (
              <>
                <div style={{ width: '60px', height: '1px', backgroundColor: '#E5E5E5', marginTop: '8px' }} />
                <p style={{ fontSize: '13px', color: '#6B6B6B', margin: 0 }}>{user.email}</p>
                <Link
                  href="/my-templates"
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '20px', color: '#2D2D2D', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <FolderOpen className="w-5 h-5" style={{ color: '#6B6B6B' }} />
                  My templates
                </Link>
                {!user.identities?.some(i => i.provider === 'google') && (
                  <button onClick={handlePasswordReset} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Roboto', fontWeight: 400, fontSize: '20px', color: '#2D2D2D', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <KeyRound className="w-5 h-5" style={{ color: '#6B6B6B' }} />
                    {resetSent ? 'Check your email!' : 'Change password'}
                  </button>
                )}
                <button onClick={() => { setIsMobileMenuOpen(false); signOut(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Roboto', fontWeight: 400, fontSize: '20px', color: '#D32F2F', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <LogOut className="w-5 h-5" />
                  Log out
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </>
  );
}
